import { useCallback, useEffect, useState } from "react";
import { Button, Table } from "antd";
import { ToastContainer, toast, Bounce } from "react-toastify";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import config from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { PERMISSIONS } from "../../constants/permissions";
import {
  buildBackupDownloadUrl,
  extractDownloadFilename,
  formatFileSize,
  readBlobErrorMessage,
} from "../../utils/backupDownload";
import "react-toastify/dist/ReactToastify.css";

const formatJalaliDateTime = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString("fa-IR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

const DatabaseBackups = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canDownload = hasPermission(PERMISSIONS.BACKUPS_DOWNLOAD);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [downloadingId, setDownloadingId] = useState(null);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await axios.get(`${config.apiBaseUrl}/api/Backups/GetList`, {
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      });
      const list = Array.isArray(response.data) ? response.data : [];
      setFiles(list);
    } catch (error) {
      const message = await readBlobErrorMessage(
        error.response?.data,
        "خطا در دریافت فهرست پشتیبان‌ها"
      );
      setLoadError(message);
      setFiles([]);
      if (error.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    fetchBackups();
  }, [fetchBackups]);

  const handleDownload = async (record) => {
    setDownloadingId(record.id);
    try {
      const response = await axios.get(buildBackupDownloadUrl(config.apiBaseUrl, record.id), {
        responseType: "blob",
        headers: { HeaderKey: config.HeaderKey },
        withCredentials: true,
      });

      const contentType = response.headers["content-type"] || "";
      if (contentType.includes("application/json")) {
        const message = await readBlobErrorMessage(response.data, "دانلود پشتیبان انجام نشد");
        toast.error(<p dir="rtl" className="font-custom">{message}</p>);
        return;
      }

      const fileName = extractDownloadFilename(
        response.headers["content-disposition"],
        record.fileName || "backup.bak"
      );
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);

      toast.success(
        <p dir="rtl" className="font-custom">
          دانلود پشتیبان شروع شد
        </p>
      );
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error(
          <p dir="rtl" className="font-custom">
            نشست شما منقضی شده است
          </p>
        );
        navigate("/");
        return;
      }

      const fallback = error.response?.status === 403
        ? "اجازه دانلود این پشتیبان را ندارید"
        : "دانلود پشتیبان انجام نشد";
      const message = await readBlobErrorMessage(error.response?.data, fallback);
      toast.error(<p dir="rtl" className="font-custom">{message}</p>);
    } finally {
      setDownloadingId(null);
    }
  };

  const columns = [
    {
      title: "نام فایل",
      dataIndex: "fileName",
      key: "fileName",
      align: "center",
      className: "font-custom",
    },
    {
      title: "حجم",
      dataIndex: "sizeBytes",
      key: "sizeBytes",
      align: "center",
      className: "font-custom",
      render: (size) => formatFileSize(size),
    },
    {
      title: "آخرین تغییر",
      dataIndex: "lastModifiedUtc",
      key: "lastModifiedUtc",
      align: "center",
      className: "font-custom",
      render: (value) => formatJalaliDateTime(value),
    },
    {
      title: "دانلود",
      key: "download",
      align: "center",
      className: "font-custom",
      render: (_, record) => (
        <Button
          type="primary"
          className="font-custom"
          disabled={!canDownload || downloadingId === record.id}
          loading={downloadingId === record.id}
          onClick={() => handleDownload(record)}
        >
          دانلود
        </Button>
      ),
    },
  ];

  return (
    <div className="font-custom rtl text-right p-5">
      <h1 className="text-center text-xl mt-5 mb-5">پشتیبان پایگاه داده</h1>
      <div className="flex justify-end mb-4">
        <Button onClick={fetchBackups} loading={loading} className="font-custom">
          بارگذاری مجدد
        </Button>
      </div>

      {loadError && (
        <p className="text-red-600 text-center mb-4" dir="rtl">
          {loadError}
        </p>
      )}

      <div className="w-full shadow-lg">
        <Table
          columns={columns}
          dataSource={files}
          loading={loading}
          rowKey={(record) => record.id}
          locale={{ emptyText: loadError ? "فهرست پشتیبان‌ها دریافت نشد" : "فایل پشتیبان وجود ندارد" }}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            pageSizeOptions: ["5", "10", "20", "50"],
            showTotal: (total, range) => `${range[0]}-${range[1]} از ${total}`,
          }}
          bordered
        />
      </div>

      <ToastContainer
        position="bottom-center"
        autoClose={5000}
        rtl
        pauseOnHover
        theme="dark"
        transition={Bounce}
        className="font-custom text-center"
      />
    </div>
  );
};

export default DatabaseBackups;
